import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CustomTooltip from '@components/CustomTooltip';

import useClickOutside from '@hooks/useClickOutside';

import { likeMaterial } from '@store/material/action';
import {
	getSearchValue,
	setIsDescriptionSearchOpen,
	updateSearchResultForFabric
} from '@store/supplier/action';
import { removeItemFromGlobalFilter, updateTextItemInGlobalFilter } from '@store/actions/FilterAction';

import { checkAnyUserType, getUserPreference } from '@/utils/Util';
import { setSystemUserPreferences } from '@/services/commonService';
import {
	MATERIAL_TOGGLE_OPTIONS,
	whichUserMaterialEditAndDuplicateAndDeleteCheck
} from '@/utils/MaterialsUtil';
import CommonImage from '@/components/CommonImage';

const FabricSearchWithQR = ({
	toggleOptions,
	setToggleOptions,
	setIsEdit,
	setSelectedItem,
	onSetLikeAndUnlike,
	setIsInventoryModal,
	setIsIssueTab
}) => {
	const [isShowDropdown, setIsShowDropdown] = useState(false);
	const dropdownRef = useRef(null);
	const inputRef = useRef(null);
	const dispatch = useDispatch();
	const { materialId, isSuccess, searchKey } = useSelector((store) => store?.supplier?.fabricSearch);

	useEffect(() => {
		if (isSuccess && materialId && toggleOptions.IS_ADD_MATERIAL_FAVOURITE_TOGGLE_ON) {
			dispatch(likeMaterial(materialId, onSetLikeAndUnlike));
		}
		if (isSuccess && materialId && toggleOptions.IS_EDIT_MATERIAL_TOGGLE_ON) {
			setIsEdit('FABRIC');
			setSelectedItem({ id: materialId });
		}
		if (isSuccess && materialId && toggleOptions.IS_VIEW_MATERIAL_DETAILS_TOGGLE_ON) {
			window.open(`/materials/view/${materialId}?type=FABRIC`, '_blank');
		}
		if (isSuccess && materialId && toggleOptions.IS_ISSUE_FABRIC_TOGGLE_ON) {
			setSelectedItem({ id: materialId });
			setIsInventoryModal(true);
			setIsIssueTab(true);
		}
		return () => {
			dispatch(updateSearchResultForFabric(null, false));
		};
	}, [isSuccess, materialId]);

	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus();
		}
	}, [toggleOptions.IS_SEARCH_WITH_QR_CODE, toggleOptions.IS_SEARCH_WITH_NAME_AND_REF_NO]);

	useEffect(() => {
		if (
			!toggleOptions.IS_SEARCH_WITH_NAME_AND_REF_NO &&
			!toggleOptions.IS_SEARCH_WITH_SUPPLIER_REF_NO &&
			!toggleOptions.IS_SEARCH_WITH_QR_CODE &&
			!toggleOptions.IS_SEARCH_WITH_FABRIC_TITLE_OR_COLOR
		) {
			setToggleOptions((prev) => ({
				...prev,
				IS_SEARCH_WITH_NAME_AND_REF_NO: true
			}));
		}
	}, []);

	const setSeachValue = (value) => {
		dispatch(getSearchValue(value));
	};

	const handleDescriptionSearchOpen = (value) => {
		dispatch(setIsDescriptionSearchOpen(value));
		setIsShowDropdown(false);
	};

	const handleOutsideClick = () => {
		setIsShowDropdown(false);
	};

	useClickOutside(dropdownRef, handleOutsideClick);

	const handleUpdateUserPreference = async (updatedToggleOptions) => {
		const userPreference = getUserPreference();
		await setSystemUserPreferences({
			...userPreference,
			...updatedToggleOptions
		});
	};

	const handleToggle = async (preferenceKey, preferenceValue) => {
		setToggleOptions((prev) => ({
			...prev,
			[preferenceKey]: preferenceValue
		}));

		handleUpdateUserPreference({
			[preferenceKey]: preferenceValue
		});
	};

	const handleSelectSearchOption = (preferenceKey) => {
		const updatedToggleOptions = {
			[MATERIAL_TOGGLE_OPTIONS.IS_SEARCH_WITH_QR_CODE]: false,
			[MATERIAL_TOGGLE_OPTIONS.IS_SEARCH_WITH_NAME_AND_REF_NO]: false,
			[MATERIAL_TOGGLE_OPTIONS.IS_SEARCH_WITH_SUPPLIER_REF_NO]: false,
			[MATERIAL_TOGGLE_OPTIONS.IS_SEARCH_WITH_FABRIC_TITLE_OR_COLOR]: false,
			[preferenceKey]: true
		};

		setToggleOptions((prev) => ({
			...prev,
			...updatedToggleOptions
		}));

		handleUpdateUserPreference(updatedToggleOptions);
	};

	const getPlaceholderBasedOnCondition = () => {
		if (toggleOptions.IS_SEARCH_WITH_QR_CODE) {
			return 'QR Code';
		}
		if (toggleOptions.IS_SEARCH_WITH_NAME_AND_REF_NO) {
			return 'Nitex Ref. No.';
		} else if (toggleOptions.IS_SEARCH_WITH_SUPPLIER_REF_NO) {
			return 'Supplier Ref. No.';
		} else if (toggleOptions.IS_SEARCH_WITH_FABRIC_TITLE_OR_COLOR) {
			return 'Fabric Title/Color';
		} else {
			return '';
		}
	};

	const getIdentifierBasedOnCondition = () => {
		if (toggleOptions.IS_SEARCH_WITH_QR_CODE || toggleOptions.IS_SEARCH_WITH_NAME_AND_REF_NO) {
			return 'nitexReferenceNumber';
		} else if (toggleOptions.IS_SEARCH_WITH_SUPPLIER_REF_NO) {
			return 'supplierReferenceNumber';
		}
		return 'search';
	};

	const setSearchResultToStore = (value) => {
		const currentIdentifier = getIdentifierBasedOnCondition();
		const conflictingKeys = ['search', 'nitexReferenceNumber', 'supplierReferenceNumber'].filter(
			(key) => key !== currentIdentifier
		);

		conflictingKeys.forEach((key) => {
			dispatch(removeItemFromGlobalFilter({ name: key, identifier: key }));
		});

		const data = {
			name: currentIdentifier,
			type: 'text',
			key: value,
			value: value,
			identifier: currentIdentifier,
			isHideChip: false
		};

		dispatch(updateTextItemInGlobalFilter(data));
	};

	const getQRCode = (e) => {
		const qrCode = e.target.value.split(':')[0] || '';
		setSearchResultToStore(qrCode.trim());
	};

	const getSearchReasult = (e) => {
		setSearchResultToStore(e.target.value);
	};

	const handleSearch = (e) => {
		if (e.key === 'Enter') {
			if (toggleOptions.IS_SEARCH_WITH_QR_CODE) {
				getQRCode(e);
			} else {
				getSearchReasult(e);
			}
		}
	};

	const isAllowedToOpenMaterialToggle = () => {
		return !toggleOptions.IS_VIEW_MATERIAL_DETAILS_TOGGLE_ON && !toggleOptions.IS_ISSUE_FABRIC_TOGGLE_ON;
	};

	const isAllowedToOpenViewDetails = () => {
		return !toggleOptions.IS_EDIT_MATERIAL_TOGGLE_ON && !toggleOptions.IS_ISSUE_FABRIC_TOGGLE_ON;
	};

	const isAllowedToOpenIssueFabric = () => {
		return !toggleOptions.IS_VIEW_MATERIAL_DETAILS_TOGGLE_ON && !toggleOptions.IS_EDIT_MATERIAL_TOGGLE_ON;
	};

	return (
		<div className="d-flex align-items-center gap-2">
			<input
				type="text"
				autoComplete="off"
				name="name"
				value={searchKey}
				onChange={(e) => setSeachValue(e.target.value)}
				placeholder={getPlaceholderBasedOnCondition()}
				ref={inputRef}
				onKeyDown={handleSearch}
			/>
			<div className="edit-items search-edit-items">
				<div className="option">
					<div className="dropdown">
						<button
							className="btn btn-default dropdown-toggle d-flex align-items-center gap-2"
							id="menu1"
							onClick={() => setIsShowDropdown((prev) => !prev)}
						>
							<CommonImage src={`/icons/search-dark.svg`} alt="Search" />
							<CommonImage src={`/images/chevron_down.svg`} alt="Chevron down" />
						</button>
						{isShowDropdown && (
							<ul
								className="dropdown-menu dropdown-menu-right shadow-lg show"
								ref={dropdownRef}
							>
								<li
									className={`${
										toggleOptions.IS_SEARCH_WITH_NAME_AND_REF_NO ? 'active' : ''
									}`}
									onClick={() => {
										handleSelectSearchOption(
											MATERIAL_TOGGLE_OPTIONS.IS_SEARCH_WITH_NAME_AND_REF_NO
										);
									}}
								>
									<a className="gray_dark_01 regular-14">Search with nitex Ref. No.</a>
								</li>
								<li
									className={`${
										toggleOptions.IS_SEARCH_WITH_SUPPLIER_REF_NO ? 'active' : ''
									}`}
									onClick={() => {
										handleSelectSearchOption(
											MATERIAL_TOGGLE_OPTIONS.IS_SEARCH_WITH_SUPPLIER_REF_NO
										);
									}}
								>
									<a className="gray_dark_01 regular-14">Search with supplier Ref. No.</a>
								</li>

								<li
									className={`${toggleOptions.IS_SEARCH_WITH_QR_CODE ? 'active' : ''}`}
									onClick={() => {
										handleSelectSearchOption(
											MATERIAL_TOGGLE_OPTIONS.IS_SEARCH_WITH_QR_CODE
										);
									}}
								>
									<a className="gray_dark_01 regular-14">Search with QR code</a>
								</li>
								<li
									className={`${
										toggleOptions.IS_SEARCH_WITH_FABRIC_TITLE_OR_COLOR ? 'active' : ''
									}`}
									onClick={() => {
										handleSelectSearchOption(
											MATERIAL_TOGGLE_OPTIONS.IS_SEARCH_WITH_FABRIC_TITLE_OR_COLOR
										);
									}}
								>
									<a className="gray_dark_01 regular-14">Search with fabric title/color</a>
								</li>
								<li
									onClick={() => {
										handleDescriptionSearchOpen(true);
									}}
								>
									<a className="gray_dark_01 regular-14">Search with AI</a>
								</li>
								<li className="d-flex align-items-center border-top-solid justify-content-between">
									<a className="d-flex align-items-center border-top-solid">
										Add to favourite
									</a>

									<CommonImage
										className="cursor-pointer pr-3"
										src={`${
											!toggleOptions?.IS_ADD_MATERIAL_FAVOURITE_TOGGLE_ON
												? `/icons/toggle-off.svg`
												: `/icons/toggle-on.svg`
										}`}
										alt="Price Toggle"
										onClick={() =>
											handleToggle(
												MATERIAL_TOGGLE_OPTIONS.IS_ADD_MATERIAL_FAVOURITE_TOGGLE_ON,
												!toggleOptions?.IS_ADD_MATERIAL_FAVOURITE_TOGGLE_ON
											)
										}
									/>
								</li>
								{whichUserMaterialEditAndDuplicateAndDeleteCheck() && (
									<li className="d-flex align-items-center border-top-solid justify-content-between">
										<a className="gray_dark_01 regular-14">Edit material</a>
										{!isAllowedToOpenMaterialToggle() ? (
											<CustomTooltip
												arrowCenter={true}
												text="Disable Issue Fabruc and View details"
											>
												<CommonImage
													className="cursor-pointer pr-3"
													src={`${
														!toggleOptions?.IS_EDIT_MATERIAL_TOGGLE_ON
															? `/icons/toggle-off.svg`
															: `/icons/toggle-on.svg`
													}`}
													alt="Price Toggle"
													onClick={() =>
														isAllowedToOpenMaterialToggle() &&
														handleToggle(
															MATERIAL_TOGGLE_OPTIONS.IS_EDIT_MATERIAL_TOGGLE_ON,
															!toggleOptions?.IS_EDIT_MATERIAL_TOGGLE_ON
														)
													}
												/>
											</CustomTooltip>
										) : (
											<CommonImage
												className="cursor-pointer pr-3"
												src={`${
													!toggleOptions?.IS_EDIT_MATERIAL_TOGGLE_ON
														? `/icons/toggle-off.svg`
														: `/icons/toggle-on.svg`
												}`}
												alt="Price Toggle"
												onClick={() =>
													isAllowedToOpenMaterialToggle() &&
													handleToggle(
														MATERIAL_TOGGLE_OPTIONS.IS_EDIT_MATERIAL_TOGGLE_ON,
														!toggleOptions?.IS_EDIT_MATERIAL_TOGGLE_ON
													)
												}
											/>
										)}
									</li>
								)}

								<li className="d-flex align-items-center border-top-solid justify-content-between">
									<a className="gray_dark_01 regular-14">View details</a>
									{!isAllowedToOpenViewDetails() ? (
										<CustomTooltip
											arrowCenter={true}
											text="You Need to disable edit material and Issue fabric"
										>
											<CommonImage
												className="cursor-pointer pr-3"
												src={`${
													!toggleOptions?.IS_VIEW_MATERIAL_DETAILS_TOGGLE_ON
														? `/icons/toggle-off.svg`
														: `/icons/toggle-on.svg`
												}`}
												alt="Price Toggle"
												onClick={() =>
													isAllowedToOpenViewDetails() &&
													handleToggle(
														MATERIAL_TOGGLE_OPTIONS.IS_VIEW_MATERIAL_DETAILS_TOGGLE_ON,
														!toggleOptions?.IS_VIEW_MATERIAL_DETAILS_TOGGLE_ON
													)
												}
											/>
										</CustomTooltip>
									) : (
										<CommonImage
											className="cursor-pointer pr-3"
											src={`${
												!toggleOptions?.IS_VIEW_MATERIAL_DETAILS_TOGGLE_ON
													? `/icons/toggle-off.svg`
													: `/icons/toggle-on.svg`
											}`}
											alt="Price Toggle"
											onClick={() =>
												isAllowedToOpenViewDetails() &&
												handleToggle(
													MATERIAL_TOGGLE_OPTIONS.IS_VIEW_MATERIAL_DETAILS_TOGGLE_ON,
													!toggleOptions?.IS_VIEW_MATERIAL_DETAILS_TOGGLE_ON
												)
											}
										/>
									)}
								</li>
								{checkAnyUserType(['INVENTORY_MANAGEMENT', 'ADMIN']) && (
									<li className="d-flex align-items-center border-top-solid justify-content-between">
										<a className="gray_dark_01 regular-14">Issue fabric</a>
										{!isAllowedToOpenIssueFabric() ? (
											<CustomTooltip
												arrowCenter={true}
												text="You need to disable View Details and Edit material"
											>
												<CommonImage
													className="cursor-pointer pr-3"
													src={`${
														!toggleOptions?.IS_ISSUE_FABRIC_TOGGLE_ON
															? `/icons/toggle-off.svg`
															: `/icons/toggle-on.svg`
													}`}
													alt="Price Toggle"
													onClick={() =>
														isAllowedToOpenIssueFabric() &&
														handleToggle(
															MATERIAL_TOGGLE_OPTIONS.IS_ISSUE_FABRIC_TOGGLE_ON,
															!toggleOptions?.IS_ISSUE_FABRIC_TOGGLE_ON
														)
													}
												/>
											</CustomTooltip>
										) : (
											<CommonImage
												className="cursor-pointer pr-3"
												src={`${
													!toggleOptions?.IS_ISSUE_FABRIC_TOGGLE_ON
														? `/icons/toggle-off.svg`
														: `/icons/toggle-on.svg`
												}`}
												alt="Price Toggle"
												onClick={() =>
													isAllowedToOpenIssueFabric() &&
													handleToggle(
														MATERIAL_TOGGLE_OPTIONS.IS_ISSUE_FABRIC_TOGGLE_ON,
														!toggleOptions?.IS_ISSUE_FABRIC_TOGGLE_ON
													)
												}
											/>
										)}
									</li>
								)}
							</ul>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default FabricSearchWithQR;
